import clsx from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { BillingTrialStatus } from '@gitbook/api-client';
import { canPlanMigrateToNewPricing } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    Heading,
    SidebarAlert,
    SidebarAlertActions,
    SidebarButton,
} from '@gitbook/spine-react';

import {
    getTrialEndingNotice,
    UpgradeDialogProvider,
    useUpgradeDialogContext,
} from '../../Components';
import { calculateDaysDifference, useTrial } from '../../state';

/**
 * Display an alert in the sidebar body for an organization.
 * It doesn't suspend rendering while fetching the billing to prevent making
 * the whole sidebar lag uselessly.
 */
export function OrganizationAlert(props: {
    organization: api.Organization;
    className?: string;
    onCollapseTrial?: () => void;
}): React.ReactElement | null {
    const { organization, className, onCollapseTrial } = props;
    const canAdminBilling = organization.permissions.admin;
    const status = organization.trial.status;

    // First: show the alert to upgrade if the organization's trial has ended

    /*
     * Explicitly check if trialEnd is set, as BillingTrialStatus can be 'expired' even without a trialEnd.
     * See the special case for orgs created < 2022-09-06 in getOrganizationTrialStatus.
     */
    const hasTrialEndDate = Boolean(organization.trial.endDate);
    if (status === BillingTrialStatus.Active && hasTrialEndDate) {
        return (
            <UpgradeDialogProvider>
                <BillingTrialAlert
                    organization={organization}
                    canAdminBilling={canAdminBilling}
                    className={className}
                    onCollapse={onCollapseTrial}
                />
            </UpgradeDialogProvider>
        );
    }

    // Second: show an alert prompting organizations to migrate to new pricing
    if (canAdminBilling && canPlanMigrateToNewPricing(organization.plan)) {
        return <BillingSelfServeUpgradeAlert organization={organization} className={className} />;
    }

    return null;
}

/**
 * Animate an organization trial alert between its expanded and collapsed states.
 */
export function CollapsibleOrganizationAlert(props: {
    organization: api.Organization;
    collapsed: boolean;
    onCollapse: () => void;
}): React.ReactElement | null {
    const { organization, collapsed, onCollapse } = props;
    const { animationState, isMounted, onAnimationEnd } = useCssAnimatedPresence(!collapsed);

    if (!isMounted) {
        return null;
    }

    return (
        <div
            className={clsx(
                'origin-bottom',
                animationState === 'entering' &&
                    'fade-in zoom-in-[0.92] slide-in-from-bottom-4 animate-in duration-200 ease-snappy-out',
                animationState === 'exiting' &&
                    'fade-out zoom-out-25 slide-out-to-bottom-6 animate-out duration-200 ease-in'
            )}
            onAnimationEnd={onAnimationEnd}
        >
            <OrganizationAlert
                data-org-alerts
                organization={organization}
                onCollapseTrial={onCollapse}
            />
        </div>
    );
}

/**
 * Compact control for restoring a collapsed organization trial alert.
 */
export function CollapsedOrganizationTrialAlertButton(props: {
    visible: boolean;
    onPress: () => void;
}): React.ReactElement | null {
    const { visible, onPress } = props;
    const { animationState, isMounted, onAnimationEnd } = useCssAnimatedPresence(visible);

    if (!isMounted) {
        return null;
    }

    return (
        <div
            className={clsx(
                animationState === 'entering' &&
                    'fade-in zoom-in-[0.72] animate-in fill-mode-backwards duration-200 ease-snappy-out [animation-delay:100ms]',
                animationState === 'exiting' && 'zoom-out-[0.72] animate-out duration-100 ease-in'
            )}
            onAnimationEnd={onAnimationEnd}
        >
            <SidebarButton
                iconOnly
                outlined
                rounded
                label="Show trial details"
                icon={Icon.Upgrade}
                className="!border-upgrade-interactive !bg-upgrade-surface-subtle hover:!bg-upgrade-ui data-[pressed=true]:!bg-upgrade-ui-hover [&>svg]:!text-upgrade-low"
                onPress={onPress}
            />
        </div>
    );
}

function useCssAnimatedPresence(visible: boolean) {
    const [animationState, setAnimationState] = React.useState<
        'entering' | 'entered' | 'exiting' | 'exited'
    >(visible ? 'entered' : 'exited');
    const previousVisible = React.useRef(visible);

    React.useLayoutEffect(() => {
        if (previousVisible.current === visible) {
            return;
        }

        previousVisible.current = visible;
        setAnimationState(visible ? 'entering' : 'exiting');
    }, [visible]);

    return {
        animationState,
        isMounted: animationState !== 'exited',
        onAnimationEnd: (event: React.AnimationEvent<HTMLDivElement>) => {
            if (event.target !== event.currentTarget) {
                return;
            }

            setAnimationState(visible ? 'entered' : 'exited');
        },
    };
}

/**
 * Alert when the trial has finished or is about to finish.
 */
function BillingTrialAlert({
    organization,
    canAdminBilling,
    className,
    onCollapse,
}: {
    organization: api.Organization;
    canAdminBilling: boolean;
    className?: string;
    onCollapse?: () => void;
}) {
    const trial = useTrial({ organization });

    const reason = 'sidebar_cta';
    const { openUpgradeDialog } = useUpgradeDialogContext();

    return (
        <SidebarAlert
            className={className}
            data-testid="sidebar.trialAlert"
            onCollapse={onCollapse}
        >
            <Heading>
                {getTrialEndingNotice({
                    daysLeft: trial.daysLeft,
                })}
            </Heading>
            <Description>
                {canAdminBilling
                    ? trial.status === BillingTrialStatus.Active
                        ? 'Upgrade at any time to continue accessing paid features.'
                        : 'Subscribe today to keep all the features you’ve tried.'
                    : 'Ask your admin to subscribe to keep all the features you’ve tried.'}
            </Description>
            <SidebarAlertActions>
                {canAdminBilling ? (
                    <div className="flex flex-grow flex-col gap-2">
                        <Button
                            label="Upgrade"
                            kind="upgrade"
                            leadingIcon={Icon.Upgrade}
                            size="small"
                            onPress={() => {
                                openUpgradeDialog({
                                    organization,
                                    targetProduct: api.BillingProduct.Pro2024,
                                    reason,
                                });
                            }}
                        />
                        <Button
                            kind="secondary"
                            size="small"
                            label={
                                trial.status === BillingTrialStatus.Active
                                    ? 'Cancel trial'
                                    : 'Downgrade to free'
                            }
                            onPress={() => {
                                openUpgradeDialog({
                                    organization,
                                    targetProduct: api.BillingProduct.Free2024,
                                    reason,
                                });
                            }}
                        />
                    </div>
                ) : null}
            </SidebarAlertActions>
        </SidebarAlert>
    );
}

/**
 * Alert when the the organization can upgrade to new pricing
 */
function BillingSelfServeUpgradeAlert({
    organization,
    className,
}: {
    organization: api.Organization;
    className?: string;
}) {
    if (!organization.internal_billingMigration) {
        return;
    }

    const { deadline, discountPercent } = organization.internal_billingMigration;

    if (!deadline || !discountPercent) {
        return;
    }

    const daysLeft = calculateDaysDifference(new Date(), new Date(deadline));

    return (
        <SidebarAlert className={className}>
            <Heading>Your pricing plan is changing</Heading>
            <Description>
                Your current plan will be automatically migrated{' '}
                {daysLeft > 0 ? (
                    <>
                        in <strong>{daysLeft}</strong> {daysLeft === 1 ? 'day' : 'days'}
                    </>
                ) : (
                    'soon'
                )}
                . Switch now to save <strong>{discountPercent}% for up to 12 months</strong>!
            </Description>
            <SidebarAlertActions>
                <Button
                    label="Switch now and save"
                    kind="upgrade"
                    size="small"
                    href={{
                        overlay: {
                            type: 'org_settings',
                            organization: organization.id,
                            tab: { type: 'upgrade' },
                            hash: 'sidebar_cta',
                        },
                    }}
                />
            </SidebarAlertActions>
        </SidebarAlert>
    );
}
